import { computed, ref } from 'vue'

/**
 * Countdown timer updating in specified intervals
 * @param timeout The countdown time in milliseconds
 * @param options Optional settings for the countdown
 * @returns An object containing the remaining time and control functions
 */
export function useCountdown(
  timeout: number,
  options?: {
    interval?: number
    onComplete?: () => unknown
  }
) {
  /** The time remaining in milliseconds. */
  const remaining = ref(0)
  const intervalId = ref<ReturnType<typeof setInterval> | undefined>(undefined)
  const { interval = 100, onComplete } = options ?? {}

  function update() {
    remaining.value = Math.max(0, remaining.value - interval)
    if (remaining.value <= 0) {
      onComplete?.()
      stop()
    }
  }

  function start() {
    if (intervalId.value) return
    if (remaining.value <= 0) {
      reset()
    }
    intervalId.value = setInterval(update, interval)
  }

  function stop() {
    if (intervalId.value) {
      clearInterval(intervalId.value)
      intervalId.value = undefined
    }
  }

  function reset() {
    stop()
    remaining.value = timeout
  }

  return {
    remaining,
    start,
    stop,
    reset,
  }
}

/**
 * Visual countdown timer in minutes:seconds format
 * Counts down in 1 second intervals
 * @param onComplete Callback function to be called when the countdown reaches zero
 * @returns An object containing the remaining time and control functions
 */
export function createCountdown(onComplete: () => void) {
  const remainingSeconds = ref<number>(0)
  const timer = ref<number | undefined>(undefined)

  function start(seconds: number) {
    if (timer.value) {
      throw new Error('Attempted to start countdown when countdown is already running.')
    }
    remainingSeconds.value = seconds
    timer.value = window.setInterval(tick, 1000)
  }

  function stop() {
    window.clearInterval(timer.value)
    timer.value = undefined
    remainingSeconds.value = 0
  }

  function tick() {
    remainingSeconds.value -= 1
    if (remainingSeconds.value <= 0) {
      onComplete()
      stop()
    }
  }

  const timeLeft = computed(() => {
    const seconds = String(remainingSeconds.value % 60).padStart(2, '0')
    const minutes = String(parseInt((remainingSeconds.value / 60).toString(), 10)).padStart(2, '0')
    return `${minutes}:${seconds}`
  })

  return {
    start,
    stop,
    timeLeft,
    remainingSeconds,
  }
}
